import React, { useEffect, useState } from 'react';
import {
  alpha, Box, Dialog, DialogContent, DialogTitle, IconButton, Paper, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ImageIcon from '@mui/icons-material/Image';
import { __t } from '../../../i18n/t';
import {
  BLOCK_TYPES, ROW_ART, ROW_LAYOUTS, ROW_LAYOUT_DESCRIPTION, ROW_LAYOUT_LABEL, RowLayout,
} from './homeItems';

const FULL_LAYOUT: RowLayout = 'full';

interface AddBlockDialogProps {
  open: boolean;
  onClose(): void;
  onPick(layout: RowLayout): void;
}

const CARD_SX = {
  flex: 1,
  p: 2,
  cursor: 'pointer',
  textAlign: 'center' as const,
  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
};

/**
 * Card de escolha de tipo, no mesmo desenho do seletor de tipo de desconto
 * (`FormDiscountType`): tile tonal pequeno à esquerda e texto à direita. Um
 * retângulo vazio do tamanho do card com um ícone solto no meio é lido como
 * imagem que não carregou.
 */
const TYPE_CARD_SX = {
  flex: 1,
  p: 2.5,
  cursor: 'pointer',
  borderRadius: 2,
  transition: 'all 0.18s ease',
  '&:hover': {
    borderColor: 'primary.main',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
    transform: 'translateY(-1px)',
    boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.18)}`,
  },
};

/** Miniatura do layout: é ela que explica a escolha, não o texto. */
const LayoutGlyph: React.FC<{ layout: RowLayout }> = ({ layout }) => {
  // Fora do JSX: o plugin de i18n embrulha literal em posição de texto, e
  // `layout === __t('full')` nunca casaria em espanhol.
  const isFull = layout === FULL_LAYOUT;
  const slot = {
    height: 54,
    borderRadius: 0.75,
    bgcolor: 'primary.main',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'primary.contrastText',
  };
  return (
    <Box sx={{
      display: 'flex', gap: 0.75, p: 1, mb: 1.5, borderRadius: 1,
      bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider',
    }}>
      {isFull ? (
        <Box sx={{ ...slot, flex: 1 }}><ImageIcon fontSize="small" /></Box>
      ) : (
        <>
          <Box sx={{ ...slot, flex: 1 }}><ImageIcon fontSize="small" /></Box>
          <Box sx={{ ...slot, flex: 1 }}><ImageIcon fontSize="small" /></Box>
        </>
      )}
    </Box>
  );
};

/**
 * Duas etapas dentro do MESMO modal: primeiro o tipo de bloco, depois o layout da
 * linha. O tipo é uma etapa própria (hoje com uma opção só) para o dia em que houver
 * bloco de texto, vitrine de produto e afins — a escolha não fica presa a imagem.
 */
const AddBlockDialog: React.FC<AddBlockDialogProps> = ({ open, onClose, onPick }) => {
  const [kind, setKind] = useState<string | null>(null);

  useEffect(() => { if (open) { setKind(null); } }, [open]);

  const typeStep = (
    <>
      <DialogTitle>Que tipo de bloco?</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, pt: 1, pb: 2 }}>
          {BLOCK_TYPES.map(type => (
            <Paper key={type.kind} variant="outlined" onClick={() => setKind(type.kind)} sx={TYPE_CARD_SX}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: 1.5, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                }}>
                  <ImageIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ lineHeight: 1.3, mb: 0.4 }}>
                    {__t(type.label)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block' }}>
                    {__t(type.description)}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      </DialogContent>
    </>
  );

  const layoutStep = (
    <>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton size="small" onClick={() => setKind(null)} aria-label="Voltar">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        Como essa linha fica?
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, pt: 1, pb: 2 }}>
          {ROW_LAYOUTS.map(layout => {
            const art = ROW_ART[layout];
            return (
              <Paper key={layout} variant="outlined" onClick={() => onPick(layout)} sx={CARD_SX}>
                <LayoutGlyph layout={layout} />
                <Typography variant="subtitle2">{__t(ROW_LAYOUT_LABEL[layout])}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {__t(ROW_LAYOUT_DESCRIPTION[layout])}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {`Arte recomendada: ${art.width} × ${art.height} px`}
                </Typography>
              </Paper>
            );
          })}
        </Box>
      </DialogContent>
    </>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {kind ? layoutStep : typeStep}
    </Dialog>
  );
};

export default AddBlockDialog;
